import React from 'react';
import {MdCheckbox, MdTextField} from 'core/components/fields/fields';
import {FIELDDEFDISPLAYTYPE} from 'core/form/form';
import {createForm} from 'core/form/createForm';
import {lovValue, FormProps, ClosedContentProps, lovLabel, FieldText} from 'core/form/formUtils';
import StyledSelect from 'core/components/fields/styledSelect';
import NumberField from 'core/components/fields/numberField';
import {BlockComp} from 'core/components/blockComp/blockComp';
import {Validate} from 'core/form/validate';
import {NumberFormat, Min, Max, HasLength, IsNumber} from 'core/form/rules';
import {optionsSearch} from "core/common/service/commonDataService";
import {InvoiceTemplate} from "invoice/domain/invoiceDomain";
import {action, observable} from "mobx";
import {observer} from "mobx-react";
import {PARTYROLE} from "party/partyDomain";
import {GridRow} from "core/grid/domain/grid";
import {StatusActiveComp, StatusActiveComp__h5_style} from "core/components/buttons/statusActiveComp";
import commonService from "core/common/service/commonService";
import StyledDatePicker from "core/components/fields/styledDatePicker";
import Checkbox from "material-ui/Checkbox";
import ActiveItem from "core/components/blockComp/activeItem";
import {formatDate} from "core/common/utils/dateUtils";
import styled from "styled-components";
import {EVENTTYPE} from "core/event/eventDomain";
import {grey400} from "material-ui/styles/colors";
import MdField, {DATATYPE} from "../../core/metamodel/mdField";
import {loc} from "core/common/config/locales";
import securityService from "core/security/securityService";
import {SECURABLE} from "core/metamodel/metaConstant";
import mdEntityService from "../../core/metamodel/mdEntityService";
import {DynamicFieldsBlock} from "../../core/metamodel/dynamicFields";


const HeadingDiv = styled.div`
    font-size: 18px;
    color: ${grey400}
`;


interface InvoiceTemplateFormProps extends FormProps<InvoiceTemplate, InvoiceTemplate> {
  recalculate: (rootObject: InvoiceTemplate, modifiedRows: GridRow[], toDeleteRows: GridRow[], params?: { [key: string]: any }) => Promise<any>;
}


const ClosedContent: any = observer(({dataObject, entity, entities, fields, disabled}: ClosedContentProps<InvoiceTemplate>) => (
  <div style={{width: '100%'}}>

    <div style={{display: 'flex'}}>
      {disabled ? null :
        <StatusActiveComp activeObject={dataObject} entity={entity} id={dataObject.invoiceTemplateId}/>}
      <h5 style={StatusActiveComp__h5_style}> {lovLabel(dataObject.name)}</h5>
    </div>

    {(dataObject.description) ? (<div><i>{dataObject.description}</i></div>): null}


    <div className="row">
      <div className="col-xs-12 col-sm-6">
        <HeadingDiv>Customer</HeadingDiv>
        {(dataObject.billedTo) ? (
          <div className="form-text-row">
            <span className="zz-mr-5">{loc.i18n`invoice.billed-to`} {(dataObject.billedTo2) ? " 1" : ""}</span>
            <FieldText value={dataObject.billedTo.label}
                       linkProps={{to: 'partyDetail', params: {id: dataObject.billedTo.value}}}/>
            {(dataObject.orderedBy) ? (
              <FieldText label='Ordered By' value={dataObject.orderedBy.label}
                         linkProps={{to: 'partyDetail', params: {id: dataObject.orderedBy.value}}}/>
            ) : null}
            {(dataObject.issuedBy) ? (
              <FieldText label='Issued By' value={dataObject.issuedBy.label}
                         linkProps={{to: 'partyDetail', params: {id: dataObject.issuedBy.value}}}/>
            ) : null}
          </div>
        ) : null}
        {(dataObject.billedTo2) ? (
          <div className="form-text-row">
            <span className="zz-mr-5">{loc.i18n`invoice.billed-to`} 2</span>
            <FieldText value={dataObject.billedTo2.label}
                       linkProps={{to: 'partyDetail', params: {id: dataObject.billedTo2.value}}}/>
          </div>
        ) : null}
      </div>
      <div className="col-xs-12 col-sm-6">
        <HeadingDiv>Details</HeadingDiv>
        <div className="form-text-row">
          <FieldText label={fields.invoiceType.textLabel} value={dataObject.invoiceType}/>
          <FieldText label={fields.invoiceCurrency.textLabel} value={dataObject.invoiceCurrency}/>
          <FieldText label={fields.invoiceAdjustmentKoef.textLabel} value={dataObject.invoiceAdjustmentKoef}/>
          <span className="zz-ml-5 zz-mr-5">{(dataObject.issueInvoice)? 'Issue Invoice' : 'Leave is Concept'}</span>
          <FieldText label={fields.eventType.textLabel} value={dataObject.eventType}/>
        </div>
        <div className="form-text-row">
          <FieldText label={fields.startInvoicePeriodExpression.textLabel} value={dataObject.startInvoicePeriodExpression}/>
          <FieldText label={fields.endInvoicePeriodExpression.textLabel} value={dataObject.endInvoicePeriodExpression}/>
        </div>
        <div className="form-text-row">
          <FieldText label={fields.invoiceDateExpression.textLabel} value={dataObject.invoiceDateExpression}/>
          <FieldText label={fields.taxDateExpression.textLabel} value={dataObject.taxDateExpression}/>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-xs-12 col-sm-6">
        <HeadingDiv>Scheduling</HeadingDiv>
        {dataObject.disableScheduling ?
          <span className="zz-ml-5 zz-mr-5">Disable Scheduling</span>
          :
          <div>
            <div className="form-text-row">
              <span
                className="zz-ml-2">{`Repeat once every ${dataObject.remittanceSkipCount} ${lovLabel(dataObject.remittanceType)} starting at ${formatDate(dataObject.validFrom)} ${(!dataObject.validTo) ? 'forever' : ('to ' + formatDate(dataObject.validTo))}`}</span>
            </div>
            <div className="form-text-row">
              <FieldText label={fields.lastGenerateDate.textLabel} value={dataObject.lastGenerateDate}
                         mdField={fields.lastGenerateDate.mdField}/>
              <FieldText label={fields.nextGenerateDate.textLabel} value={dataObject.nextGenerateDate}
                         mdField={fields.nextGenerateDate.mdField}/>
            </div>
          </div>
        }
      </div>
      <div className="col-xs-12 col-sm-6">
        <HeadingDiv>Customer Overrides</HeadingDiv>
        <div className="form-text-row">
          <FieldText label={fields.paymentCond.textLabel} value={dataObject.paymentCond}/>
          <FieldText label={fields.profitCenter.textLabel} value={dataObject.profitCenter}/>
          <FieldText label={fields.language.textLabel} value={dataObject.language}/>
          <FieldText label={fields.monetaryRounding.textLabel} value={dataObject.monetaryRounding}/>
          <FieldText label={fields.entityNumberGen.textLabel} value={dataObject.entityNumberGen ? dataObject.entityNumberGen.label : 'Default'}/>
        </div>
        <div className="form-text-row">
          <FieldText label={fields.accountingNumber.textLabel} value={dataObject.accountingNumber}/>
          <FieldText label={fields.accountingProfitCenter.textLabel} value={dataObject.accountingProfitCenter}/>
          <FieldText label={fields.costCenter.textLabel} value={dataObject.costCenter}/>
          <FieldText label={fields.internalOrder.textLabel} value={dataObject.internalOrder}/>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-xs-12 col-sm-6">

        <HeadingDiv>Comments</HeadingDiv>
        <div className="form-text-row">
          <FieldText value={dataObject.comment}/>
        </div>
        <div className="form-text-row">
          <FieldText label={fields.internalComment.textLabel} value={dataObject.internalComment}/>
        </div>

      </div>
      <div className="col-xs-12 col-sm-6">
        <HeadingDiv>Parameters</HeadingDiv>
        {
          dataObject.fields.map(dynField => {
            let field = entity.getField(dynField.fieldName);
            if (!field) return null;
            return (
              <div className="form-text-row">
                <FieldText mdField={field} label={field.formLabelActive} value={dynField.value}/>
              </div>
            )
          })
        }
      </div>
    </div>



  </div>
));

@createForm({
  formName: 'InvoiceTemplateForm',
  fields: [{
    name: 'name',
    validators: ['IsRequired']
  }, {
    name: 'description',
    displayType: FIELDDEFDISPLAYTYPE.textArea
  }, {
    name: 'profitCenter',
  }, {
    name: 'paymentCond'
  }, {
    name: 'paymentType'
  }, {
    name: 'invoiceCurrency',
    validators: ['IsRequired']
  }, {
    name: 'language'
  }, {
    name: 'monetaryRounding'
  }, {
    name: 'invoiceAdjustmentKoef',
    validators: ['IsRequired', 'IsNumber']
  }, {
    name: 'issuedBy'
  }, {
    name: 'orderedBy'
  }, {
    name: 'billedTo'
  }, {
    name: 'billedTo2'
  }, {
    name: 'comment',
    displayType: FIELDDEFDISPLAYTYPE.textArea
  }, {
    name: 'internalComment',
    displayType: FIELDDEFDISPLAYTYPE.textArea
  }, {
    name: 'disableScheduling',
    displayType: FIELDDEFDISPLAYTYPE.checkbox,
    mdField: new MdField({label: 'Disable Scheduling', dataType: DATATYPE.BOOLEAN })
  }, {
    name: "remittanceType",
    validators: ['IsRequired'],
    omitNullOption: true,
    usePlaceholder: true
  }, {
    name: "remittanceSkipCount",
    validators: ['IsRequired'],
    usePlaceholder: true
  }, {
    name: "validFrom",
    validators: ['IsRequired'],
    usePlaceholder: true
  }, {
    name: "validTo",
    usePlaceholder: true
  }, {
    name: "holidayHandling"
  }, {
    name: "issueInvoice"
  }, {
    name: "markAsSentByPost"
  }, {
    name: "lastGenerateDate"
  }, {
    name: "nextGenerateDate"
  }, {
    name: "startInvoicePeriodExpression"
  }, {
    name: "endInvoicePeriodExpression"
  },{
    name: "periodicInvoice"
  }, {
    name: "invoiceDateExpression"
  }, {
    name: "taxDateExpression"
  }, {
    name: "eventType"
  }, {
    name: "entityNumberGen"
  }, {
    name: "accountingNumber"
  }, {
    name: "internalOrder"
  }, {
    name: "accountingProfitCenter"
  }, {
    name: "costCenter"
  }, {
    name: "invoiceType",
    validators: ['IsRequired'],
    omitNullOption: true
  }]
}, ClosedContent)
@observer
export default class InvoiceTemplateForm extends React.Component<InvoiceTemplateFormProps, InvoiceTemplateFormProps> {

  @observable forever: boolean;

  componentWillMount() {
    let {entities, dataObject, form} = this.props;
    if (dataObject.$new) {
      dataObject.disableScheduling = true;
      form.updateFieldsPropValue(dataObject, "disableScheduling");
    }
    form.getField("orderedBy").props.options = dataObject.getTuple("orderedByOptions");
    this.forever = (!dataObject.validTo);
  }


  @action recalculate = (evt?: any) => {
    return this.props.recalculate(this.props.dataObject, null, null);
  };

  @action handleForever = (evt) => {
    let {entities, dataObject, form, rootObject} = this.props;
    this.forever = !this.forever;
    if (this.forever) {
      dataObject.validTo = null;
      form.updateFieldsPropValue(dataObject, "validTo");
      rootObject.$dirty = true;
    }
  };



  @action onBilledToChange = () => {
    let {entities, dataObject, form} = this.props;
    console.log('onBilledToChange ', dataObject.billedTo);

    let partyId = lovValue(dataObject.billedTo);

    let optionsPromise = (partyId != null) ? optionsSearch('Party', {contactsOf: partyId}, '*') : Promise.resolve({options: []});


    return Promise.all([this.props.recalculate(this.props.dataObject, null, null, {billedToChange: true}), optionsPromise])
      .then(action("onBilledToChange-cb1", ([x, options]) => {
        form.getField("orderedBy").props.options = options.options;
        dataObject.orderedBy = (options.options.length === 1) ? options.options[0].value : null;
        if (dataObject.billedTo == null) {
          dataObject.disableScheduling = true;
        }
        form.updateAllFieldsPropValue(dataObject);
        if (dataObject.getTuple("vatRateChange")) {
          commonService.notifyInfo("Vat rates were changed");
        }
      }));

  };

  render() {


    let {dataObject, rootObject, entity, entities, form, formName} = this.props;
    let fields = form.fields;
    let {
      profitCenter, paymentCond, invoiceCurrency, language, monetaryRounding, comment, internalComment, issuedBy, orderedBy, invoiceAdjustmentKoef, markAsSentByPost,
      billedTo, billedTo2, disableScheduling, remittanceType, remittanceSkipCount, validFrom, validTo, name, description, issueInvoice, startInvoicePeriodExpression, endInvoicePeriodExpression, periodicInvoice,
      invoiceDateExpression, taxDateExpression, eventType, entityNumberGen, accountingNumber, costCenter, accountingProfitCenter, internalOrder, invoiceType
    } = dataObject.getFormFields(formName);

    let clearBilledTo2 = () => {
      billedTo2.props.value = undefined;
      dataObject.billedTo2 = undefined
    }

    let eventTypeOptions =  eventType.props.options.filter(li => li.label === '---' || li["eventType"] === EVENTTYPE.INVOICE_INTERNAL_EMAIL );

    let strings = securityService.currentUser.detailIdList(SECURABLE.invoiceTemplateDetail.name);
    let invoiceTypeOptions = invoiceType.props.options.filter(li => strings.includes(lovValue(li).toString()));

    const openContent = (

      <form style={{marginTop: 10}}>

        <BlockComp>
          <div className="row">
            <div className="fx col-xs-4">
              <NumberField {...name.props} />
              <Validate field={name} value={name.props.value}/>
            </div>
            <div className="fx col-xs-8">
              <MdTextField {...description.props} multiLine={true} rows={1}/>
            </div>
          </div>
        </BlockComp>


        <div className="row">
          <div className="col-xs-12 col-sm-6">

            <BlockComp className="block-comp-right-border" header="Customer" style={{marginBottom: 0}}>
              <div className="row">
                <div className="fx col-xs-12 col-sm-8">
                  <StyledSelect  {...billedTo.props} async={true}
                                 loadOptions={optionsSearch.bind(this, 'Party', {role: [PARTYROLE.CUSTOMER_ROLE]})}
                                 clearable={true} onValueEmpty={clearBilledTo2} />
                  <Validate field={billedTo} value={billedTo.props.value} onSuccess={this.onBilledToChange}/>
                </div>
                <div className="fx col-xs-12 col-sm-4">
                  <StyledSelect  {...orderedBy.props} clearable={false}/>
                </div>
                <div className="fx col-xs-12 col-sm-8">
                  <StyledSelect  {...billedTo2.props} async={true}
                                 loadOptions={optionsSearch.bind(this, 'Party', {role: [PARTYROLE.CUSTOMER_ROLE]})}
                                 clearable={true} disabled={!billedTo.props.value} />
                  <Validate field={billedTo2} value={billedTo2.props.value}/>
                </div>
                <div className="fx col-xs-12 col-sm-4">
                  <StyledSelect  {...issuedBy.props} async
                                 loadOptions={optionsSearch.bind(this, 'Party', {role: [PARTYROLE.USER_ROLE]})}
                                 clearable
                                 asterixForAll/>
                </div>
              </div>
            </BlockComp>

          </div>


          <div className="col-xs-12 col-sm-6">

            <BlockComp header="Scheduling" style={{marginBottom: 0}}>
              <div className="row">
                <div className="col-xs-11">
                  <MdCheckbox {...disableScheduling.props} disabled={dataObject.billedTo == null || dataObject.items.length == 0}/>
                </div>
              </div>

              {!dataObject.disableScheduling &&
              <div>
                <div style={{display: 'flex', flexWrap: 'wrap'}}>
                  <span style={{marginRight: 5, position: 'relative', top: 26}}>Repeat once every </span>
                  <div className="fx col-xs-1">
                    <NumberField {...remittanceSkipCount.props}  />
                    <Validate field={remittanceSkipCount} value={remittanceSkipCount.props.value}>
                      <NumberFormat precision={2} scale={0}/>
                      <Min min={1}/>
                      <Max max={11}/>
                    </Validate>
                  </div>
                  <div className="fx col-xs-3">
                    <StyledSelect  {...remittanceType.props} clearable={false}/>
                    <Validate field={remittanceType} value={remittanceType.props.value}/>
                  </div>
                </div>

                <div style={{display: 'flex', flexWrap: 'wrap'}}>
                  <span style={{marginRight: 5, position: 'relative', top: 26}}>from </span>
                  <div className="fx col-xs-3">
                    <StyledDatePicker {...validFrom.props}  />
                    <Validate field={remittanceType} value={remittanceType.props.value}/>
                  </div>
                  <div className="fx col-xs-2" style={{position: 'relative', top: 26}}>
                    <Checkbox onCheck={this.handleForever} label="forever" checked={this.forever}
                              labelStyle={{marginLeft: -10}}
                    />
                  </div>
                  {(!this.forever) ? (
                    <span style={{marginLeft: 5, marginRight: 5, position: 'relative', top: 26}}>to </span>
                  ) : null}

                  {(!this.forever) ? (
                    <div className="fx col-xs-3">
                      <StyledDatePicker {...validTo.props} hintText=""/>
                    </div>
                  ) : null}
                </div>
              </div>
              }

            </BlockComp>

          </div>

        </div>


        <div className="row">
          <div className="col-xs-12 col-sm-6">

            <BlockComp className="block-comp-right-border" header="Details" style={{marginBottom: 0}}>
              <div className="row">
                <div className="fx col-xs-6">
                  <StyledSelect {...invoiceCurrency.props} />
                  <Validate field={invoiceCurrency} value={invoiceCurrency.props.value}/>
                </div>
                <div className="fx col-xs-6">
                  <NumberField {...invoiceAdjustmentKoef.props} />
                  <Validate field={invoiceAdjustmentKoef} value={invoiceAdjustmentKoef.props.value}
                            onSuccess={this.recalculate}/>
                </div>
              </div>

              <div className="row">
                <div className="col-xs-11">
                  <MdCheckbox {...periodicInvoice.props} style={{height: 30, marginTop: 10}}/>
                </div>
              </div>

              {dataObject.periodicInvoice &&
              <div className="row">
                <div className="fx col-xs-6">
                  <MdTextField {...startInvoicePeriodExpression.props} />
                </div>
                <div className="fx col-xs-6">
                  <MdTextField {...endInvoicePeriodExpression.props} />
                </div>
              </div>
              }

              <div className="row">
                <div className="fx col-xs-6">
                  <MdTextField {...invoiceDateExpression.props} />
                </div>
                <div className="fx col-xs-6">
                  <MdTextField {...taxDateExpression.props} />
                </div>
                <div className="col-xs-6">
                  <MdCheckbox {...issueInvoice.props} style={{height: 30}} />
                  { (dataObject.issueInvoice) ? <MdCheckbox {...markAsSentByPost.props} style={{height: 30}}/> : null}
                </div>
                <div className="fx col-xs-6">
                  <StyledSelect  {...eventType.props} options={eventTypeOptions} />
                </div>
              </div>
            </BlockComp>

            <BlockComp className="block-comp-right-border" header="Customer Overrides" style={{marginBottom: 0}}>
              <div className="row">

                <div className="fx col-xs-6">
                  <StyledSelect {...paymentCond.props} />
                </div>
                <div className="fx col-xs-6">
                  <StyledSelect {...profitCenter.props} />
                </div>
                <div className="fx col-xs-6">
                  <StyledSelect {...language.props} />
                </div>
                <div className="fx col-xs-6">
                  <NumberField {...monetaryRounding.props} />
                  <Validate field={monetaryRounding} value={monetaryRounding.props.value}>
                    <NumberFormat precision={1} scale={0}/>
                    <Min min={0}/>
                    <Max max={6}/>
                  </Validate>
                </div>
              </div>
            </BlockComp>


          </div>

          <div className="col-xs-12 col-sm-6">

            <BlockComp header="Comments" style={{marginBottom: 0}}>

              <div className="row">
                <div className="fx col-xs-12">
                  <MdTextField {...comment.props} multiLine={true} rows={2}/>
                </div>
              </div>
              <div className="row">
                <div className="fx col-xs-12">
                  <MdTextField {...internalComment.props} multiLine={true} rows={2}/>
                </div>
              </div>

              <div className="row">
                <div className="fx col-xs-6">
                  <StyledSelect  {...entityNumberGen.props} async
                                 loadOptions={optionsSearch.bind(this, 'EntityNumberGen', {entityName: "Invoice"})}
                                 clearable
                                 asterixForAll/>
                </div>
                <div className="fx col-xs-6">
                  <StyledSelect {...invoiceType.props} options={invoiceTypeOptions} />
                  <Validate field={invoiceType} value={invoiceType.props.value}/>
                </div>
              </div>

              <div className="row">
                <div className="fx col-xs-6">
                  <StyledSelect {...accountingNumber.props} />
                </div>
                <div className="fx col-xs-6">
                  <MdTextField {...internalOrder.props} />
                </div>
              </div>

              <div className="row">
                <div className="fx col-xs-6">
                  <StyledSelect {...accountingProfitCenter.props} />
                </div>
                <div className="fx col-xs-6">
                  <StyledSelect  {...costCenter.props} />
                </div>
              </div>

            </BlockComp>

            <DynamicFieldsBlock header="Parameters" dataObject={dataObject} rootObject={dataObject} entity={entity} entities={mdEntityService.entityMap} />

          </div>

        </div>


      </form>


    );

    return (
      <ActiveItem openContent={openContent}
                  closedContent={<ClosedContent {...{dataObject, entity, entities, fields}} />}
                  validate={this.props.validate} {...this.props} />
    )
  }
}





// WEBPACK FOOTER //
// ./src/invoice/component/invoiceTemplateForm.tsx